// src/features/fourlink-config/components/shared/ChecklistModal.jsx

import React, { useState, useEffect } from 'react';

const IconX = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconFileText = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 13h6M9 17h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconAlertCircle = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M12 8v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="16" r="1" fill="currentColor" />
  </svg>
);

const IconCheckCircle = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M8 12l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconClock = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChecklistModal = ({ 
  isOpen, 
  onClose, 
  checklistData,
  isLoading = false,
  error = null 
}) => {
  const [activeTab, setActiveTab] = useState('items');

  // Resetear tab cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setActiveTab('items');
    }
  }, [isOpen]);

  // Cerrar con ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Bloquear scroll del body cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Cargando checklist...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <IconAlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <p className="text-red-600 font-medium">Error al cargar el checklist</p>
            <p className="text-gray-500 text-sm mt-2">{error}</p>
          </div>
        </div>
      );
    }

    if (!checklistData) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <IconFileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No hay datos del checklist</p>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Header con información general */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {checklistData.nombre || 'Sin nombre'}
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                {checklistData.descripcion || 'Sin descripción'}
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <IconCheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-gray-700">
                    <strong>{checklistData.items?.filter(i => i.completado).length || 0}</strong>
                    {' / '}
                    {checklistData.items?.length || 0} completados
                  </span>
                </div>
                {checklistData.fechaCreacion && (
                  <div className="flex items-center gap-2">
                    <IconClock className="h-4 w-4 text-gray-500" />
                    <span className="text-gray-600">
                      Creado: {new Date(checklistData.fechaCreacion).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {checklistData.items && checklistData.items.length > 0 && (
              <div className="ml-4">
                <div className="relative w-16 h-16">
                  <svg className="w-16 h-16 transform -rotate-90">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="#e5e7eb"
                      strokeWidth="6"
                      fill="none"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="#10b981"
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 28}`}
                      strokeDashoffset={`${2 * Math.PI * 28 * (1 - (checklistData.items.filter(i => i.completado).length / checklistData.items.length))}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold text-gray-700">
                      {Math.round((checklistData.items.filter(i => i.completado).length / checklistData.items.length) * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('items')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'items'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Items del Checklist
              {checklistData.items && (
                <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                  {checklistData.items.length}
                </span>
              )}
            </button>
            {checklistData.comentarios && checklistData.comentarios.length > 0 && (
              <button
                onClick={() => setActiveTab('comments')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'comments'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Comentarios
                <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                  {checklistData.comentarios.length}
                </span>
              </button>
            )}
          </nav>
        </div>

        {/* Contenido de tabs */}
        <div className="space-y-3">
          {activeTab === 'items' && renderItems()}
          {activeTab === 'comments' && renderComments()}
        </div>
      </>
    );
  };

  const renderItems = () => {
    if (!checklistData.items || checklistData.items.length === 0) {
      return (
        <div className="text-center py-8">
          <IconFileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay items en este checklist</p>
        </div>
      );
    }

    return checklistData.items.map((item, index) => (
      <div
        key={item.id || index}
        className={`border rounded-lg p-4 transition-all ${
          item.completado
            ? 'bg-green-50 border-green-200'
            : 'bg-white border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 mt-1 h-5 w-5 rounded border-2 flex items-center justify-center ${
            item.completado
              ? 'bg-green-500 border-green-500'
              : 'border-gray-300'
          }`}>
            {item.completado && (
              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-medium ${item.completado ? 'text-green-900 line-through' : 'text-gray-900'}`}>
              {item.titulo || item.nombre || `Item ${index + 1}`}
            </p>
            {item.descripcion && (
              <p className="text-sm text-gray-600 mt-1">{item.descripcion}</p>
            )}
            {item.notas && (
              <div className="mt-2 text-sm bg-yellow-50 border border-yellow-200 rounded p-2">
                <p className="text-yellow-800">{item.notas}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
              {item.categoria && (
                <span className="bg-gray-100 px-2 py-1 rounded">
                  {item.categoria}
                </span>
              )}
              {item.prioridad && (
                <span className={`px-2 py-1 rounded ${
                  item.prioridad === 'alta' ? 'bg-red-100 text-red-700' :
                  item.prioridad === 'media' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  Prioridad: {item.prioridad}
                </span>
              )}
              {item.fechaCompletado && (
                <span className="text-gray-500">
                  Completado: {new Date(item.fechaCompletado).toLocaleDateString('es-ES')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    ));
  };

  const renderComments = () => {
    if (!checklistData.comentarios || checklistData.comentarios.length === 0) {
      return (
        <div className="text-center py-8">
          <IconFileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay comentarios en este checklist</p>
        </div>
      );
    }

    return checklistData.comentarios.map((comentario, index) => (
      <div key={comentario.id || index} className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-700 font-medium text-sm">
              {comentario.usuario?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 mb-1">
              <p className="font-medium text-gray-900">
                {comentario.usuario || 'Usuario desconocido'}
              </p>
              <span className="text-xs text-gray-500">
                {comentario.fecha ? new Date(comentario.fecha).toLocaleString('es-ES') : ''}
              </span>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{comentario.texto}</p>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="absolute inset-0 overflow-hidden flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <IconFileText className="h-6 w-6 text-blue-600" />
              Detalles del Checklist
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
            >
              <IconX className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {renderContent()}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
